/**
 * MainLayout Component
 *
 * Main application layout using synapse design system AppShell.
 */

import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { AppShell, type NavItem } from '@lognai/synapse-design-system-v2';
import { ClipboardList, Inbox, PackageCheck, PanelsTopLeft, ReceiptText, Settings, Ship, ShoppingCart, Sparkles } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useNavigation } from '../hooks/useNavigation';
import { useIsExternalPartner } from '../hooks/useIsExternalPartner';
import { useSidebarMode } from '../hooks/useSidebarMode';
import { useSidebarCollapsed } from '../hooks/useSidebarCollapsed';
import { UserMenu } from '../components/UserMenu';
import { Logo, LogoMark } from '../components/Logo';
import { AskLognAISearchBar, ChatSidebar, CommandPalette, useKeyboardShortcut } from '@lognai/plugin-ai-core-ui';

// Adapter component to make React Router's Link work with AppShell's href prop
const RouterLink = React.forwardRef<HTMLAnchorElement, any>(
  ({ href, children, ...props }, ref) => (
    <Link to={href} ref={ref} {...props}>
      {children}
    </Link>
  )
);
RouterLink.displayName = 'RouterLink';

export default function MainLayout() {
  const { user } = useAuth();
  const isExternalPartner = useIsExternalPartner();
  const { setMode: setSidebarMode } = useSidebarMode();
  const { collapsed: sidebarCollapsed, toggleCollapsed } = useSidebarCollapsed();

  // The side rail is a permanent dark "command deck": the brand navy holds in
  // both light and dark app themes so the nav is always read against a deep,
  // high-contrast surface. It no longer follows the content theme.
  useEffect(() => {
    setSidebarMode('dark');
  }, [setSidebarMode]);
  const location = useLocation();
  const navigate = useNavigate();
  const [aiOpen, setAiOpen] = useState(false);
  const [aiInitialPrompt, setAiInitialPrompt] = useState<string | null>(null);
  const [aiInitialThreadId, setAiInitialThreadId] = useState<string | null>(null);
  const [aiContextOverride, setAiContextOverride] = useState<{
    pluginId: string;
    entityType: string;
    entityId: string;
    url: string;
  } | null>(null);
  const [aiContextSession, setAiContextSession] = useState(0);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Derive pageContext from the current route. Covers the entity detail pages
  // that 1C cares about; extend as more domains add detail surfaces.
  const deriveAiPageContext = (pathname: string): { pluginId: string; entityType: string; entityId: string; url: string } | undefined => {
    // Paths reflect the actual plugin-UI route prefixes (see defineRoutes
    // calls in each *-ui/src/routes.ts).
    const patterns: Array<[RegExp, { pluginId: string; entityType: string }]> = [
      [/^\/orders\/purchase\/([^/]+)/, { pluginId: 'orders', entityType: 'purchaseOrder' }],
      [/^\/orders\/sales\/([^/]+)/, { pluginId: 'orders', entityType: 'salesOrder' }],
      [/^\/orders\/backorders\/([^/]+)/, { pluginId: 'orders', entityType: 'backorder' }],
      [/^\/shipments\/([^/]+)/, { pluginId: 'shipments', entityType: 'shipment' }],
      [/^\/inventory\/items\/([^/]+)/, { pluginId: 'inventory', entityType: 'inventoryItem' }],
      [/^\/inventory\/lots\/([^/]+)/, { pluginId: 'inventory', entityType: 'lot' }],
      [/^\/inventory\/serials\/([^/]+)/, { pluginId: 'inventory', entityType: 'serial' }],
      [/^\/products\/([^/]+)/, { pluginId: 'products', entityType: 'product' }],
      [/^\/warehouse\/warehouses\/([^/]+)/, { pluginId: 'warehouse', entityType: 'warehouse' }],
    ];
    for (const [re, meta] of patterns) {
      const m = pathname.match(re);
      if (m && m[1] && !['new', 'create'].includes(m[1])) {
        return { ...meta, entityId: m[1], url: pathname };
      }
    }
    return undefined;
  };
  const pageContext = deriveAiPageContext(location.pathname);

  useKeyboardShortcut({
    key: 'k',
    metaKey: true,
    ctrlKey: true,
    onTrigger: () => setPaletteOpen((o) => !o),
  });

  useEffect(() => {
    const h = (e: Event) => {
      const ce = e as CustomEvent<{
        prompt?: string;
        pageContext?: {
          pluginId: string;
          entityType: string;
          entityId: string;
          url: string;
        };
      }>;
      if (ce.detail?.prompt) setAiInitialPrompt(ce.detail.prompt);
      setAiContextOverride(ce.detail?.pageContext ?? null);
      if (ce.detail?.pageContext) setAiContextSession((current) => current + 1);
      setAiOpen(true);
    };
    window.addEventListener('lognai:open-ai-chat', h);
    return () => window.removeEventListener('lognai:open-ai-chat', h);
  }, []);

  // Get dynamic navigation from route registry
  const { navItems: navItemConfigs, isLoading } = useNavigation();

  // Convert NavItemConfig[] to NavItem[] for AppShell
  const convertToNavItems = (items: any[]): NavItem[] => {
    return items.map((item) => ({
      id: item.id,
      href: item.path || '/',
      icon: item.icon,
      text: item.label,
      isSeparator: item.isSeparator,
      isGroupHeader: item.isGroupHeader,
      disabled: item.disabled,
      subItems: item.children
        ? item.children.map((child: any) => ({
            href: child.path || '/',
            text: child.label,
          }))
        : undefined,
    }));
  };

  // Plugin-driven nav, with a disabled "VMI Selection (Inactive)" placeholder
  // slotted just below the dynamic "VMI Inventory" group — outside it, as its
  // own item (after the group's separator). It has no route or plugin behind
  // it — it renders greyed-out and non-clickable.
  const pluginNavItems: NavItem[] = convertToNavItems(navItemConfigs);
  const vmiSeparatorIdx = pluginNavItems.findIndex((it) => it.id === 'separator-inventory');
  const vmiHeaderIdx = pluginNavItems.findIndex((it) => it.id === 'header-inventory');
  const vmiInsertIdx = vmiSeparatorIdx !== -1 ? vmiSeparatorIdx + 1 : vmiHeaderIdx !== -1 ? pluginNavItems.length : -1;
  if (vmiInsertIdx !== -1) {
    pluginNavItems.splice(vmiInsertIdx, 0, {
      id: 'vmi-selection',
      href: '#',
      text: 'VMI Selection (Inactive)',
      icon: <PackageCheck size={20} />,
      disabled: true,
    });
  }

  // "FleetMon" — an external link slotted inside the dynamic "Shipments" group
  // (before its separator, so it reads as a Shipments item). Opens in a new tab.
  const shipSeparatorIdx = pluginNavItems.findIndex((it) => it.id === 'separator-shipments');
  const shipHeaderIdx = pluginNavItems.findIndex((it) => it.id === 'header-shipments');
  const fleetmonInsertIdx = shipSeparatorIdx !== -1 ? shipSeparatorIdx : shipHeaderIdx !== -1 ? pluginNavItems.length : -1;
  if (fleetmonInsertIdx !== -1) {
    pluginNavItems.splice(fleetmonInsertIdx, 0, {
      id: 'fleetmon',
      href: 'https://fleetmon.lognai.com/',
      text: 'FleetMon',
      icon: <Ship size={20} />,
      external: true,
    });
  }

  // Flat items that belong next to the dynamic "Shipments" group: the planner
  // feeds it, the audit and ProcureAI follow from it. None has a nav group of
  // its own, so they are positioned around that group here rather than sitting
  // in the static list below.
  const shipmentPlannerItem: NavItem = {
    id: 'shipment-planner',
    href: '/shipment-planner',
    text: 'Shipment Planner',
    icon: <ClipboardList size={20} />,
    subItems: [
      { href: '/shipment-planner', text: 'Dashboard' },
      { href: '/shipment-planner/routes', text: 'Route Planner' },
    ],
  };
  const belowShipmentsItems: NavItem[] = [
    {
      id: 'freight-audit',
      href: '/freight-audit',
      text: 'Freight Audit',
      icon: <ReceiptText size={20} />,
    },
    {
      id: 'procureai',
      href: 'https://procureai.lognai.com/',
      text: 'ProcureAI',
      icon: <ShoppingCart size={20} />,
      external: true,
    },
  ];

  // `shipments` is company-gated while these three are not, so a company without
  // it must still get them — they fall back to the static list below.
  const hasShipmentsGroup = shipHeaderIdx !== -1;
  if (hasShipmentsGroup) {
    // Insert below the group first: doing it in the other order would shift the
    // header index and put the planner in the wrong place.
    const belowIdx =
      shipSeparatorIdx !== -1
        ? pluginNavItems.findIndex((it) => it.id === 'separator-shipments') + 1
        : pluginNavItems.length;
    pluginNavItems.splice(belowIdx, 0, ...belowShipmentsItems);
    pluginNavItems.splice(shipHeaderIdx, 0, shipmentPlannerItem);
  }

  // External partner/supplier users get a minimal portal: only their plugin nav
  // items (e.g. "My Application"). Internal users get the full app shell.
  const allNavItems: NavItem[] = isExternalPartner
    ? pluginNavItems
    : [
        {
          id: 'workspace',
          href: '/workspace',
          text: 'Workspace',
          icon: <PanelsTopLeft size={20} />,
        },
        // Normally slotted around the dynamic "Shipments" group above; only
        // rendered here when that group is absent for this company.
        ...(hasShipmentsGroup ? [] : [shipmentPlannerItem, ...belowShipmentsItems]),
        ...pluginNavItems,
        {
          id: 'insights',
          href: '/insights',
          text: 'Insights',
          icon: <Sparkles size={20} />,
          badge: (
            <span
              className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold leading-none tracking-wide text-white"
              style={{ background: 'var(--primary)' }}
            >
              AI
            </span>
          ),
        },
        {
          id: 'drafts',
          href: '/drafts',
          text: 'Drafts',
          icon: <Inbox size={20} />,
        },
        {
          id: 'settings',
          href: '/settings',
          text: 'Settings',
          icon: <Settings size={20} />,
        },
      ];

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div>Loading...</div>
      </div>
    );
  }

  const analyticsPresentation = location.pathname.startsWith('/analytics/dashboards/')
    && new URLSearchParams(location.search).get('present') === '1';

  if (analyticsPresentation) {
    return <div className="min-h-screen w-full bg-background"><Outlet /></div>;
  }

  return (
    <AppShell
      title=""
      currentPath={location.pathname}
      LinkComponent={RouterLink as never}
      navigationItems={allNavItems}
      collapsed={sidebarCollapsed}
      onToggleCollapsed={toggleCollapsed}
      logo={
        <Logo
          // The rail is always the dark command deck, so the logo always uses
          // its dark-surface variant regardless of the content theme.
          surface="dark"
          className="h-12 w-auto"
        />
      }
      collapsedLogo={<LogoMark className="h-8 w-8" />}
      headerContent={
        <>
          <AskLognAISearchBar
            onClick={(query) => {
              setAiInitialThreadId(null);
              if (query) setAiInitialPrompt(query);
              setAiOpen(true);
            }}
            onOpenThread={(threadId) => {
              setAiInitialPrompt(null);
              setAiInitialThreadId(threadId);
              setAiOpen(true);
            }}
          />
          <div className="ml-auto pr-6">
            <UserMenu user={user} />
          </div>
        </>
      }
    >
      <div className="w-full px-6 py-6">
        <Outlet />
      </div>
      <ChatSidebar
        key={aiContextOverride ? `context-${aiContextSession}` : 'global'}
        open={aiOpen}
        onClose={() => {
          setAiOpen(false);
          setAiInitialPrompt(null);
          setAiInitialThreadId(null);
          setAiContextOverride(null);
        }}
        pageContext={aiContextOverride ?? pageContext}
        initialPrompt={aiInitialPrompt ?? undefined}
        initialThreadId={aiInitialThreadId ?? undefined}
      />
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onNavigate={(target) => navigate(target)}
        onAskAi={(q) => {
          setPaletteOpen(false);
          setAiInitialPrompt(q);
          setAiOpen(true);
        }}
      />
    </AppShell>
  );
}
